/**
 * Stockaily consent foundation — centralized, first-party, vendor-neutral
 * service (WS-Consent, 2026-08-02).
 *
 * Single source of truth for the site-wide cookie / privacy-preference
 * state.  Every UI surface (banner, settings dialog, footer button,
 * future analytics providers) reads and writes consent through this
 * module.  Direct reads from `localStorage` for consent state are
 * forbidden elsewhere in the app.
 *
 * Design constraints (see spec sections 2, 3, 7):
 *
 *   * Strict privacy-first defaults:
 *       necessary  : true    (always, never toggleable)
 *       functional : false
 *       analytics  : false
 *       marketing  : false
 *
 *   * Absence of a stored decision is NEVER consent.
 *   * Corrupted or partially-written state fails closed to defaults.
 *   * A stored decision written under an older schema/version is NOT
 *     silently upgraded — the user is asked again.
 *   * Locale changes DO NOT invalidate a stored decision.
 *   * No unique tracking identifier is created solely for consent
 *     storage — the stored record is fully anonymous.
 *   * The stored record must not contain email, account id, portfolio
 *     data, tickers, IP, user-agent, ad ids, or cross-site ids.
 *
 * Public API:
 *
 *   readConsent()                   → immutable snapshot or null
 *   hasValidDecision()              → boolean
 *   getCategory(name)               → boolean (default-false for anything
 *                                     other than "necessary" when no
 *                                     decision exists)
 *   writeConsent(categories, opts)  → persist a full decision
 *   acceptAll()                     → convenience: turn every currently
 *                                     supported optional category on
 *   necessaryOnly()                 → convenience: keep only "necessary"
 *   withdrawAll()                   → same as necessaryOnly()
 *   updatePartial(patch)            → e.g. { analytics: false } from a
 *                                     future in-app "manage" panel
 *   subscribe(fn)                   → push notifications on every
 *                                     durable change (returns unsubscribe)
 *   registerConsentService(cfg)     → vendor-neutral lifecycle boundary
 *                                     used by WS-4 later
 *   __resetForTests()               → test-only escape hatch
 *
 * Storage: browser `localStorage`, key `ws11_consent`, matches the
 * pre-existing entry in backend `services/ws11_registry.py`
 * (`COOKIE_STORAGE` → `ws11_consent`, category `necessary`).  The
 * consent-record itself is legally necessary storage.
 */

// ---------------------------------------------------------------------
// Schema / constants
// ---------------------------------------------------------------------

/**
 * Consent schema version.  Incremented for WS-4 (2026-08-03): previous
 * consent copy described PostHog as "not active".  WS-4 turns the
 * PostHog EU integration into an operational optional service, so any
 * stored decision from before this version must be re-collected under
 * the updated wording.  Existing decisions written under an older
 * version are treated as INVALID (banner is re-shown) rather than
 * silently upgraded.
 */
export const CONSENT_SCHEMA_VERSION = 2;

export const CONSENT_STORAGE_KEY = "ws11_consent";

/**
 * The full internal category set.  Kept intentionally extensible so a
 * future workstream can add functional or marketing categories without
 * a new storage layer.  The PUBLIC settings UI must expose only the
 * categories actually offered at that point in time — see
 * VISIBLE_CATEGORIES below.
 */
export const CONSENT_CATEGORIES = Object.freeze([
  "necessary",
  "functional",
  "analytics",
  "marketing",
]);

/**
 * Strict privacy-first defaults.  Necessary is always true because
 * necessary storage (auth session, locale, the consent record itself)
 * is legally necessary to deliver the service.  Every optional
 * category is off unless the user explicitly consents.
 */
export const CONSENT_DEFAULTS = Object.freeze({
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
});

/**
 * Categories that are visible in the public settings UI RIGHT NOW.
 * Do not advertise categories/technologies that are not genuinely
 * registered for current or immediately intended use (spec section 2).
 * When WS-4 is executed, Analytics will start to have a runtime effect;
 * it is already exposed here so the user can opt in / out.  Functional
 * and marketing are internally supported but not exposed publicly
 * because there is currently no functional or marketing vendor whose
 * behaviour would depend on the toggle.
 */
export const VISIBLE_CATEGORIES = Object.freeze(["necessary", "analytics"]);

// ---------------------------------------------------------------------
// Low-level storage
// ---------------------------------------------------------------------

function _safeParse(raw) {
  if (typeof raw !== "string" || raw.length === 0) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === "object" ? v : null;
  } catch {
    return null;
  }
}

function _readRaw() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function _writeRaw(str) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, str);
  } catch {
    /* storage disabled / quota — fail closed on next read */
  }
}

function _clearRaw() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

// ---------------------------------------------------------------------
// Validation / normalization
// ---------------------------------------------------------------------

/**
 * Return a strictly-typed, fully-defaulted consent record.
 * Returns null if the record is invalid, corrupted, or on an
 * unsupported schema version — in that case the banner must be shown.
 */
function _validate(record) {
  if (!record || typeof record !== "object") return null;
  if (record.schema !== "stockaily.consent") return null;
  if (record.version !== CONSENT_SCHEMA_VERSION) return null;
  if (!record.categories || typeof record.categories !== "object") return null;
  // Required: exact category set with strict boolean values.
  const cats = {};
  for (const c of CONSENT_CATEGORIES) {
    const v = record.categories[c];
    if (typeof v !== "boolean") return null;
    cats[c] = v;
  }
  // Necessary is always true.  If a stored record ever contained
  // necessary=false we treat it as invalid — fail closed.
  if (cats.necessary !== true) return null;
  const decidedAt = typeof record.decidedAt === "string" ? record.decidedAt : null;
  const updatedAt = typeof record.updatedAt === "string" ? record.updatedAt : null;
  if (!decidedAt) return null;
  const locale = typeof record.locale === "string" ? record.locale : null;
  return Object.freeze({
    schema: "stockaily.consent",
    version: CONSENT_SCHEMA_VERSION,
    categories: Object.freeze(cats),
    decidedAt,
    updatedAt,
    locale,
  });
}

/**
 * Merge a partial `patch` onto the current defaults and pin
 * `necessary=true` so callers cannot accidentally disable it.
 */
function _mergeCategories(patch) {
  const out = { ...CONSENT_DEFAULTS };
  if (patch && typeof patch === "object") {
    for (const c of CONSENT_CATEGORIES) {
      if (typeof patch[c] === "boolean") out[c] = patch[c];
    }
  }
  out.necessary = true;
  return out;
}

// ---------------------------------------------------------------------
// Observability
// ---------------------------------------------------------------------

const _listeners = new Set();

function _emit(record) {
  for (const fn of Array.from(_listeners)) {
    try {
      fn(record);
    } catch {
      /* one bad subscriber must not break others */
    }
  }
}

/**
 * Subscribe to durable consent-state changes.  Callback is invoked
 * with the immutable snapshot (or null when consent has been cleared).
 * Returns an unsubscribe function.
 */
export function subscribe(fn) {
  if (typeof fn !== "function") return () => {};
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// ---------------------------------------------------------------------
// Vendor-neutral service registry (used by WS-4 later)
// ---------------------------------------------------------------------

/**
 * A registered consent-gated service.  Only the module owning the
 * service should register it, so it can declare its OWN cleanup keys.
 * Wildcard deletion of unrelated cookies / localStorage is forbidden
 * (spec section 7).
 */
const _services = new Map();

/**
 * Register a consent-dependent service.  The registration is
 * side-effect-free: it does NOT activate anything.  Activation runs
 * only after affirmative consent for the linked category, and
 * deactivation runs immediately when consent flips false.
 *
 * @param {object} cfg
 * @param {string} cfg.id             - unique service id, e.g. "posthog"
 * @param {string} cfg.category       - one of CONSENT_CATEGORIES
 * @param {function} [cfg.onActivate] - called with the current consent
 *                                       snapshot after affirmative
 *                                       consent flips false→true
 * @param {function} [cfg.onDeactivate] - called after consent flips
 *                                       true→false (or on withdrawal)
 * @param {string[]} [cfg.cleanupKeys] - registered service-owned
 *                                       storage keys that may be
 *                                       removed on deactivation.  MUST
 *                                       NOT include auth, billing,
 *                                       locale, portfolio, watchlist,
 *                                       or any unrelated key.
 */
export function registerConsentService(cfg) {
  if (!cfg || typeof cfg !== "object") return () => {};
  const { id, category } = cfg;
  if (typeof id !== "string" || !id) return () => {};
  if (!CONSENT_CATEGORIES.includes(category)) return () => {};
  _services.set(id, {
    id,
    category,
    onActivate: typeof cfg.onActivate === "function" ? cfg.onActivate : null,
    onDeactivate: typeof cfg.onDeactivate === "function" ? cfg.onDeactivate : null,
    cleanupKeys: Array.isArray(cfg.cleanupKeys)
      ? cfg.cleanupKeys.filter((k) => typeof k === "string" && k.length > 0)
      : [],
    _lastGranted: false,
  });
  // If consent is already granted at registration time, activate now.
  const snap = readConsent();
  const granted = _isCategoryGrantedSnapshot(snap, category);
  const svc = _services.get(id);
  if (svc && granted && svc.onActivate) {
    try {
      svc.onActivate(snap);
      svc._lastGranted = true;
    } catch {
      /* isolate faults */
    }
  }
  return () => _services.delete(id);
}

function _isCategoryGrantedSnapshot(snap, category) {
  if (!snap) return category === "necessary";
  return Boolean(snap.categories && snap.categories[category] === true);
}

function _runLifecycleTransitions(prev, next) {
  // Compare per-category grant status between the previous and next
  // durable snapshots (null = no decision → treat every optional
  // category as denied).  Only registered services are touched.  Only
  // the keys they themselves declared are cleaned up.
  for (const svc of _services.values()) {
    const wasGranted = _isCategoryGrantedSnapshot(prev, svc.category);
    const nowGranted = _isCategoryGrantedSnapshot(next, svc.category);
    if (!wasGranted && nowGranted) {
      if (svc.onActivate) {
        try {
          svc.onActivate(next);
        } catch {
          /* isolate faults */
        }
      }
      svc._lastGranted = true;
    } else if (wasGranted && !nowGranted) {
      if (svc.onDeactivate) {
        try {
          svc.onDeactivate(next);
        } catch {
          /* isolate faults */
        }
      }
      // Cleanup ONLY registered service-owned keys.  Never wildcard.
      if (typeof window !== "undefined" && svc.cleanupKeys.length > 0) {
        try {
          for (const key of svc.cleanupKeys) {
            window.localStorage.removeItem(key);
          }
        } catch {
          /* noop */
        }
      }
      svc._lastGranted = false;
    }
  }
}

// ---------------------------------------------------------------------
// Public read
// ---------------------------------------------------------------------

/**
 * Return the current durable consent snapshot, or null if:
 *   * no decision has been stored yet,
 *   * the stored record is corrupt / unreadable,
 *   * the stored record is on an unsupported schema version.
 * The caller (banner) treats null as "show the banner".
 */
export function readConsent() {
  const raw = _readRaw();
  const parsed = _safeParse(raw);
  return _validate(parsed);
}

/**
 * True iff a valid, current-version consent decision is stored.
 */
export function hasValidDecision() {
  return readConsent() !== null;
}

/**
 * Return the current granted state of a single category.  If no
 * valid decision exists, every optional category is denied.
 */
export function getCategory(name) {
  const snap = readConsent();
  return _isCategoryGrantedSnapshot(snap, name);
}

/**
 * Return a plain-object copy of the immutable snapshot, for debugging
 * / tests only.
 */
export function debugSnapshot() {
  const snap = readConsent();
  if (!snap) return null;
  return JSON.parse(JSON.stringify(snap));
}

// ---------------------------------------------------------------------
// Public write
// ---------------------------------------------------------------------

/**
 * Persist a full consent decision.  Every optional category defaults
 * to false unless explicitly set true in `patch`.  Necessary is always
 * pinned to true.  Emits to subscribers and runs registered service
 * activation / deactivation hooks.
 *
 * @param {object} patch - partial category map; missing keys default false
 * @param {object} [opts]
 * @param {string} [opts.locale] - locale at the time of the decision
 */
export function writeConsent(patch, opts) {
  const prev = readConsent();
  const cats = _mergeCategories(patch);
  const now = new Date().toISOString();
  const locale = opts && typeof opts.locale === "string" ? opts.locale : null;
  const record = {
    schema: "stockaily.consent",
    version: CONSENT_SCHEMA_VERSION,
    categories: cats,
    decidedAt: prev && prev.decidedAt ? prev.decidedAt : now,
    updatedAt: now,
    locale,
  };
  _writeRaw(JSON.stringify(record));
  const next = readConsent();
  _runLifecycleTransitions(prev, next);
  _emit(next);
  return next;
}

/**
 * Convenience: turn on every category that is currently offered in
 * the visible settings UI.  Necessary is always on.  Categories that
 * are not visible remain off.
 */
export function acceptAll(opts) {
  const patch = {};
  for (const c of VISIBLE_CATEGORIES) {
    patch[c] = true;
  }
  return writeConsent(patch, opts);
}

/**
 * Convenience: keep only necessary, deny every optional category.
 */
export function necessaryOnly(opts) {
  return writeConsent({}, opts);
}

/**
 * Withdrawal: identical to necessaryOnly() and immediately takes
 * effect for every registered service via the lifecycle hooks.
 */
export function withdrawAll(opts) {
  return necessaryOnly(opts);
}

/**
 * Merge a partial category-level patch onto the CURRENT decision.
 * If no valid decision exists, this seeds a fresh one with the patch
 * applied on top of the strict defaults.
 */
export function updatePartial(patch, opts) {
  const current = readConsent();
  const base = current ? { ...current.categories } : { ...CONSENT_DEFAULTS };
  return writeConsent({ ...base, ...(patch || {}) }, opts);
}

/**
 * Test-only: fully clear the durable consent state.  Not exposed via
 * any UI surface — spec section 7 explicitly forbids wildcard cookie
 * / storage deletion, and this function only removes the consent
 * record itself.
 */
export function __resetForTests() {
  const prev = readConsent();
  _clearRaw();
  const next = readConsent();
  _runLifecycleTransitions(prev, next);
  _emit(next);
}

// ---------------------------------------------------------------------
// Introspection (used by tests + release gate + settings UI)
// ---------------------------------------------------------------------

export function _visibleCategoriesForUi() {
  return [...VISIBLE_CATEGORIES];
}

export function _registeredServices() {
  return Array.from(_services.values()).map((s) => ({
    id: s.id,
    category: s.category,
    hasActivate: !!s.onActivate,
    hasDeactivate: !!s.onDeactivate,
    cleanupKeys: [...s.cleanupKeys],
  }));
}

/**
 * Convenience default export — the full public surface as one object
 * so consumers can `import consent from "@/lib/consent"`.
 */
const consent = {
  CONSENT_SCHEMA_VERSION,
  CONSENT_STORAGE_KEY,
  CONSENT_CATEGORIES,
  CONSENT_DEFAULTS,
  VISIBLE_CATEGORIES,
  readConsent,
  hasValidDecision,
  getCategory,
  writeConsent,
  acceptAll,
  necessaryOnly,
  withdrawAll,
  updatePartial,
  subscribe,
  registerConsentService,
  debugSnapshot,
  __resetForTests,
};

export default consent;
